'use client'

import React, { useMemo } from 'react'
import Image from 'next/image'
import { CMSLink } from "@/components/Link"
import type { SponsorsBlock as SponsorsBlockProps } from '@/payload-types'
import { bgClass } from '@/utilities/bgClass' 

type SponsorItem = {
  name?: string
  logo?: any
  url?: string | null
  id?: string | null
}

export const SponsorsBlock: React.FC<SponsorsBlockProps> = ({
  title,
  content,
  background,
  sponsors,
  cta,
}) => {
  const items: SponsorItem[] = useMemo(() => (Array.isArray(sponsors) ? sponsors : []), [sponsors])

  // Only marquee when we have enough logos to warrant scrolling
  const marquee = (items.length || 0) > 4

  // Duplicate once for seamless 0% -> -50% looping
  const loopItems: SponsorItem[] = useMemo(
    () => (marquee ? [...items, ...items] : items),
    [items, marquee],
  )

  // Smooth speed: more items => longer loop
  const durationSec = Math.max(18, Math.min(50, items.length * 4))

  // --- CTA normalizer: supports linkGroup (array), linkGroup shape { links: [...] }, or a single link ---
  const ctaLinks: any[] = useMemo(() => {
    if (!cta) return []
    if (Array.isArray(cta)) return cta
    if (Array.isArray((cta as any).links)) return (cta as any).links
    return [cta]
  }, [cta])

  return (
    <section className={`${bgClass(background)} w-full`}>
      {/* Global keyframes + classes (ensure available before track mounts) */}
      <style jsx global>{`
        @keyframes sponsors-marquee {
          0%   { transform: translate3d(0, 0, 0); }
          100% { transform: translate3d(-50%, 0, 0); }
        }
        .marquee-track {
          animation: sponsors-marquee var(--marquee-duration, 28s) linear infinite;
          will-change: transform;
        }
        .marquee:hover .marquee-track {
          animation-play-state: paused;
        }
        @media (prefers-reduced-motion: reduce) {
          .marquee-track {
            animation: none !important;
            transform: none !important;
          }
        }
      `}</style>

      <div className="container px-4 py-16 md:py-24 text-center">
        {title ? (
          <h2 className="text-3xl md:text-5xl mb-6 md:mb-8 font-medium text-mini-primary dark:text-mini-dark-primary">
            {title}
          </h2>
        ) : null}

        {content ? (
          <p className="max-w-3xl mx-auto text-mini-secondary dark:text-mini-dark-secondary text-base md:text-lg mb-12 md:mb-14">
            {content}
          </p>
        ) : null}

        {/* Static grid when few items */}
        {!marquee ? (
          <div className="mb-14 flex flex-wrap items-center justify-center gap-8 md:gap-12">
            {items.map((s, i) => {
              const media: any = s.logo
              const url = media?.url || media?.sizes?.card?.url || media?.sizes?.large?.url
              const alt = media?.alt || s.name || 'Sponsor logo'
              const Logo = (
                <div key={i} className="h-14 md:h-16 w-auto flex items-center">
                  {url ? (
                    <Image
                      src={url}
                      alt={alt}
                      width={160}
                      height={64}
                      className="object-contain max-h-16 h-auto w-auto"
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <div className="text-neutral-400 dark:text-neutral-200 text-sm">Logo</div>
                  )}
                </div>
              )
              return s.url ? (
                <a key={`${s.name ?? 's'}-${i}`} href={s.url ?? undefined} target="_blank" rel="noopener noreferrer" aria-label={s.name}>
                  {Logo}
                </a>
              ) : (
                Logo
              )
            })}
          </div>
        ) : (
          // Seamless marquee: full-width viewport; fixed item widths so mobile shows fewer logos (no squish)
          <div
            className="marquee relative mx-auto mb-14 overflow-hidden w-full"
            style={{
              WebkitMaskImage:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)',
              maskImage:
                'linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 92%, rgba(0,0,0,0) 100%)',
              ['--marquee-duration' as any]: `${durationSec}s`,
            }}
          >
            <div className="marquee-track flex items-center gap-8 md:gap-12">
              {loopItems.map((s, i) => {
                const media: any = s.logo
                const url = media?.url || media?.sizes?.card?.url || media?.sizes?.large?.url
                const alt = media?.alt || s.name || 'Sponsor logo'
                const Logo = (
                  <div
                    key={`${s.name ?? 's'}-${i}`}
                    className="h-14 md:h-20 w-40 md:w-55 flex items-center justify-center shrink-0"
                  >
                    {url ? (
                      <Image
                        src={url}
                        alt={alt}
                        width={200}
                        height={80}
                        className="object-contain max-h-16 md:max-h-20 h-auto w-auto"
                        loading="lazy"
                        decoding="async"
                      />
                    ) : (
                      <div className="text-neutral-400 dark:text-neutral-200 text-sm">Logo</div>
                    )}
                  </div>
                )
                return s.url ? (
                  <a
                    key={`${s.name ?? 's'}-link-${i}`}
                    href={s.url ?? undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s.name}
                  >
                    {Logo}
                  </a>
                ) : (
                  Logo
                )
              })}
            </div>
          </div>
        )}

        {/* CTA buttons (supports linkGroup or a single link) */}
        {ctaLinks.length > 0 && (
          <ul className="mt-6 flex flex-wrap items-center justify-center gap-4">
            {ctaLinks.map((item: any, i: number) => {
              const link = item?.link ?? item
              if (!link) return null
              const key = item?.id ?? link?.id ?? link?.url ?? link?.href ?? `cta-${i}`
              return (
                <li key={key}>
                  {/* CMSLink handles appearance/label/href from Payload */}
                  <CMSLink {...link} />
                </li>
              )
            })}
          </ul>
        )}
      </div>
    </section>
  )
}

export default SponsorsBlock
